import Rails from '@rails/ujs';
import { displaySpinner, hideSpinner } from './packs/application';
import { flashAlert } from 'orders_payment';
import { Turbo } from '@hotwired/turbo-rails';

document.addEventListener("turbo:load", () => {
  setupPaymentChoiceToggling();

  const newInitializerForm = document.querySelector('#new_initializer_form');
  if (newInitializerForm) setupNewInitializerForm(newInitializerForm);

  const initializerContainer = document.querySelector("#initializer-container-form");
  if (initializerContainer) setupEditInitializerContainer(initializerContainer);

  const changePaymentMethodButton = document.querySelector('#change-payment-method');
  if (changePaymentMethodButton) setupChangePaymentMethod(changePaymentMethodButton);
});

document.addEventListener('reinitializePaymentChoices', () => {
  setupPaymentChoiceToggling();

  const changePaymentMethodButton = document.querySelector('#change-payment-method');
  if (changePaymentMethodButton) setupChangePaymentMethod(changePaymentMethodButton);
});

/**
 * Toggles the payment choice blocs.
 * When the user clicks on a payment method, we update the radio button
 * and add the 'active-payment' class to the selected container.
 */
function setupPaymentChoiceToggling() {
  // 1. Payment method
  const paymentMethodContainer = document.querySelector('#payment-methods');
  paymentMethodContainer?.querySelectorAll('.payment-choice-bloc-container').forEach(block => {
    block.querySelector('.payment-choice-bloc')?.addEventListener('click', function () {
      const radio = block.querySelector('input[type=radio]');
      radio.click();

      paymentMethodContainer?.querySelectorAll('.payment-choice-bloc-container.active-payment')
        .forEach(container => container.classList.remove('active-payment'));

      block.classList.add('active-payment');
    });
  });

  // 2. Payment mode
  const paymentModeContainer = document.querySelector('#payment-modes');
  paymentModeContainer?.querySelectorAll('.payment-choice-bloc-container').forEach(block => {
    block.querySelector('.payment-choice-bloc')?.addEventListener('click', function () {
      const radio = block.querySelector('input[type=radio]');
      radio.click();

      paymentModeContainer?.querySelectorAll('.payment-choice-bloc-container.active-payment')
        .forEach(container => container.classList.remove('active-payment'));

      block.classList.add('active-payment');
    });
  });
}

/**
 * Initializes the new initializer form.
 *  - Handles the form submission to initialize a new order.
 *  - Shows a spinner before sending and handles success or error.
 *
 * @param {HTMLElement} form - The new initializer form element.
 */
function setupNewInitializerForm(form) {
  const submitButton = form.querySelector('#submit-initializer-form');
  const spinner = submitButton.querySelector('.spinner-border');

  form.addEventListener('ajax:beforeSend', () => {
    if (spinner) displaySpinner(spinner);
  });

  form.addEventListener('ajax:success', (event) => {
    hideSpinner(spinner);
    const [data, status, xhr] = event.detail;

    if (data.status === 'success') {
      Turbo.visit(data.redirectUrl);
    } else {
      console.error('Order error:', data.error);
      flashAlert(data.error);
    }
  });
}

/**
 * Initializes the edit initializer container form.
 *  - Handles the quantity increment and decrement buttons.
 *  - Handles the variant select changes.
 *  - Handles the gift checkbox changes.
 *
 * @param {HTMLElement} initializerContainer - The initializer container form element.
 */
function setupEditInitializerContainer(initializerContainer) {
  initializerContainer.addEventListener("click", function (e) {
    if (e.target.matches("#btn-plus, #btn-plus *")) {
      const quantityInput = e.target.closest("#btn-plus")?.parentElement.querySelector("#quantity-input");
      if (quantityInput) {
        quantityInput.stepUp();
        const editForm = initializerContainer.querySelector("#edit_initializer_form");
        if (editForm) editForm.requestSubmit();
      }
    }
    if (e.target.matches("#btn-minus, #btn-minus *")) {
      const quantityInput = e.target.closest("#btn-minus")?.parentElement.querySelector("#quantity-input");
      if (quantityInput) {
        quantityInput.stepDown();
        const editForm = initializerContainer.querySelector("#edit_initializer_form");
        if (editForm) editForm.requestSubmit();
      }
    }
  });

  // Handle variant select and gift checkbox changes
  initializerContainer.addEventListener("change", function (e) {
    const selectors = ["#edit_initializer_form select.form-control.form-input", "#edit_initializer_form input[type='radio']"]

    if (selectors.some(selector => e.target.matches(selector))) {
      const editForm = e.target.closest("form#edit_initializer_form");
      if (editForm) editForm.requestSubmit();
    }
    else if (e.target.matches(".is-gift-checkbox")) {
      setupGiftCheckbox(e, initializerContainer);
    }
  });
}

/**
 * Initializes the change payment method button.
 *  - Handles the click event to change the payment method.
 *  - Updates the payment method and redirects to the new payment method page.
 *
 * @param {HTMLElement} changePaymentMethodButton - The change payment method button.
 */
function setupChangePaymentMethod(changePaymentMethodButton) {
  changePaymentMethodButton.addEventListener('click', function (e) {
    e.preventDefault();

    const newPaymentMethod = changePaymentMethodButton.dataset.paymentMethod;

    const initializerFormData = {
      'initializer_form[order][id]': changePaymentMethodButton.dataset.orderId,
      'initializer_form[order][payment_method]': newPaymentMethod
    }

    Rails.ajax({
      url: '/orders/initializers',
      type: 'PUT',
      data: new URLSearchParams(initializerFormData).toString(),
      success: (data) => {
        const currentUrl = new URL(window.location.href);
        let path = currentUrl.pathname;

        if (path.includes('mobile-money')) {
          path = path.replace('mobile-money', 'credit-card');
        } else {
          path = path.replace('credit-card', 'mobile-money');
        }

        currentUrl.pathname = path;

        Turbo.visit(currentUrl.toString(), { action: 'replace' });
      },
      error: (data) => {
        console.error('Error changing payment method:', data.error);
        flashAlert(data.error);
      }
    });
  });
}

/**
 * Sets up the gift checkbox behavior:
 *  - When the gift checkbox changes, the function checks whether it is checked.
 *  - If checked, it removes the "d-none" class from the quantity container to display it.
 *  - If unchecked, it adds the "d-none" class to hide the quantity container and set the quantity to 1.
 *  - It then submits the form to update the order.
 *
 * @param {Event} e - The change event on the gift checkbox.
 * @param {HTMLElement} initializerContainer - The initializer container form element.
 */
function setupGiftCheckbox(e, initializerContainer) {
  const giftContainer = e.target.closest('.form-check');
  const quantityContainer = initializerContainer.querySelector('.product-quantity');

  if (giftContainer && quantityContainer) {
    if (e.target.checked) {
      quantityContainer.classList.remove('d-none');
    } else {
      quantityContainer.classList.add('d-none');
      const quantityInput = quantityContainer.querySelector('input[type=number]');
      if (quantityInput) quantityInput.value = 1;
    }
    const editForm = e.target.closest("form#edit_initializer_form");
    if (editForm) editForm.requestSubmit();
  }
}
